/* eslint-disable @typescript-eslint/no-unused-vars */

import { Injectable } from '@angular/core';
import { Observable, from, throwError, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { Assistant, CompletionMessage, UserProfile } from '@app-libs/model';
import { IAssistantContext, AssistantConfiguration } from './assistant.interfaces';
import { ChatStateService } from '../chat-state.service';
import { ChatType } from '../core/chat.types';
import { EventBusService, EventTypes } from '../../client/event-bus.service';
import { AuthService } from '../../auth/auth.service';
import { AssistantsApiService } from '../../api/assistants-api.service';
import { InstructionsApiService } from '../../api/instructions-api.service';
import { ConfigurationClientService } from '../../client/configuration-client.service';
import { AssistantInstructions } from './assistant-instructions.service';

/**
 * AssistantContext - Handles assistant configuration and context
 *
 * This service handles all assistant operations, migrated from ChatApiService
 * with the exact same logic to preserve functionality.
 */
@Injectable({
  providedIn: 'root',
})
export class AssistantContext implements IAssistantContext {
  constructor(
    private chatStateService: ChatStateService,
    private eventBus: EventBusService,
    private authService: AuthService,
    private assistantsApiService: AssistantsApiService,
    private instructionsApiService: InstructionsApiService,
    private configService: ConfigurationClientService,
    private assistantInstructions: AssistantInstructions,
  ) {}

  /**
   * Initialize assistant for chat
   * (Migrated from ChatApiService.initAssistant - exact same logic)
   */
  initAssistant(assistant: Assistant): Observable<void> {
    if (!assistant) {
      return throwError(() => new Error('Assistant not found'));
    }

    return from(this.authService.getProfile()).pipe(
      switchMap((profile) =>
        this.assistantInstructions
          .buildWelcomeMessage(assistant)
          .pipe(map((welcomeMessage) => ({ profile, welcomeMessage }))),
      ),
      tap(({ profile, welcomeMessage }) => {
        // Process the welcome message if present
        if (welcomeMessage) {
          // Create a new assistant object with the processed welcome message
          assistant = {
            ...assistant,
            welcome_message: welcomeMessage,
          } as Assistant;

          // Don't add welcome message to messages array - it should be displayed separately
          // The old implementation displayed it from assistant.welcome_message property
          // when there were no messages in the conversation
        }

        // Configure the assistant with the processed welcome message
        this.configureAssistant(assistant);

        // Set the workspace ID
        this.chatStateService.workspaceId = profile?.selected_workspace || '';
      }),
      switchMap(({ profile }) =>
        // Build assistant instructions
        this.instructionsApiService.buildAssistantInstructions(assistant, profile).pipe(
          tap((finalInstructions) => {
            if (finalInstructions) {
              this.addSystemMessage(finalInstructions);
            }
          }),
        ),
      ),
      map(() => void 0),
    );
  }

  /**
   * Configure assistant for chat
   * Enhanced to handle both use_model (forced) and model_name (preferred) configurations
   */
  configureAssistant(assistant: any): Observable<AssistantConfiguration> {
    // Prepare state updates
    const stateUpdates: any = {
      chatType: ChatType.Assistant,
      assistant: assistant,
    };

    // Handle model configuration based on assistant settings
    if (assistant.use_model) {
      // Force model selection
      stateUpdates.selectedModel = assistant.use_model;
      stateUpdates.preferredModel = null; // Clear preferred when forcing
    } else if (assistant.model_name) {
      // Set preferred model but don't override selectedModel
      stateUpdates.preferredModel = assistant.model_name;
      // Keep current selectedModel if it exists, otherwise use preferred
      if (
        !this.chatStateService.selectedModel ||
        this.chatStateService.selectedModel === this.configService.defaultModel
      ) {
        stateUpdates.selectedModel = assistant.model_name;
      }
    } else {
      // No model preference
      stateUpdates.preferredModel = null;
      stateUpdates.selectedModel =
        this.chatStateService.selectedModel || this.configService.defaultModel;
    }

    // Apply state updates
    this.chatStateService.update(stateUpdates);

    // Emit model selection event if needed
    if (assistant.use_model) {
      this.eventBus.emit({
        type: EventTypes.UPDATE_SELECTED_MODEL,
        data: assistant.use_model,
      });
    }

    // Return configuration
    const config: AssistantConfiguration = {
      assistant: assistant,
      model: assistant.use_model || this.chatStateService.selectedModel,
      temperature: assistant.use_temperature,
      maxTokens: assistant.max_tokens,
      systemMessages: [],
      instructions: assistant.instructions || '',
      welcomeMessage: assistant.welcome_message,
      isExternal: this.isAssistantChatExternal(assistant.assistant_id),
    };

    return of(config);
  }

  /**
   * Get assistant configuration
   */
  getAssistantConfiguration(assistantId: string): Observable<AssistantConfiguration> {
    return this.assistantsApiService
      .getAssistantById(assistantId)
      .pipe(switchMap((assistant) => this.configureAssistant(assistant)));
  }

  /**
   * Check if assistant chat is external
   * (Migrated from ChatApiService.isAssistantChatExternal - exact same logic)
   */
  isAssistantChatExternal(assistantId?: string): boolean {
    const assistant = this.chatStateService.assistant;
    return assistant?.external_id !== undefined && assistant.external_id !== null;
  }

  /**
   * Add system message to assistant context
   * (Migrated from ChatApiService.addSystemMessage - exact same logic)
   */
  addSystemMessage(message: string): void {
    const systemMessage: Partial<CompletionMessage> = {
      content: message,
      user_id: this.authService.getCurrentUserId(),
      role: 'system',
      created_at: new Date().toISOString(),
    };

    // Use the helper method for adding messages
    this.chatStateService.addMessage(systemMessage);
  }
}
